import LoginForm from "./login-form";
import React, { useState, useEffect } from "react";
import validator from "validator";
import { useHistory } from "react-router-dom";
import isStrongPassword from "validator/lib/isStrongPassword";
import isLength from "validator/lib/isLength";
import { Button, Alert, Spinner } from "react-bootstrap";
import { IoMdClose } from "react-icons/io";
import { BiAnalyse } from "react-icons/bi";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { useSupabaseApp } from "../../SupabaseApp";

/**
 * LoginPage Component
 * 
 * User authentication page for signing into the platform. Provides login form with email and
 * password input fields. Validates credentials using Supabase authentication. Redirects already
 * authenticated users to dashboard. Shows error alerts for failed login attempts and manages
 * loading states during authentication. Includes password strength validation and session management.
 * 
 * @returns {JSX.Element} Login page wrapper with form and authentication
 */
function LoginPage() {
  const history = useHistory();

  const { logIn, resendConfirmation, isLoggedIn } = useSupabaseApp();

  useEffect(() => {
    if (isLoggedIn()) history.push("/dashboard");
  }, []);

  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });

  // Keep track of input validation/errors
  const [response, setResponse] = useState("");

  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaTwoToken, setCaptchaTwoToken] = useState("");


  /**
   * Handle user login submission
   * Validates credentials and authenticates with Supabase
   * @param {Object} credentials - Object with email and password
   */
  async function handleLogin(credentials) {
    // Show loading alert while authenticating
    setResponse(
      <Alert className="error-message floating-alert" variant="light">
        <div>
          <div style={{ textAlign: "right" }}>
            <Button
              className="pitchdeckmessageclose"
              onClick={(event) => {
                event.preventDefault();
                setResponse("");
              }}
            >
              <IoMdClose />
            </Button>
          </div>
          <div style={{ display: "flex", paddingTop: "10px" }}>
            <div style={{ width: "90%" }}>
              <p>
                <span style={{ color: "#0f9d58" }}>
                  <BiAnalyse className="alert-icon" /> Loading
                </span>
              </p>
              <p style={{ marginLeft: "28px", fontSize: "14px" }}>
                Attempting to log in
              </p>
              <p style={{ marginLeft: "28px", fontSize: "14px" }}>
                If your waiting time is too long,{" "}
                <a
                  href="https://www.roiquant.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  contact us
                </a>
                .
              </p>
            </div>
            <div style={{ width: "10%", textAlign: "right" }}>
              <Spinner animation="border" variant="secondary" />
            </div>
          </div>
        </div>
      </Alert>
    );

    try {
      // Attempt to log in with credentials and CAPTCHA token
      await logIn(credentials.email, credentials.password, captchaToken);

      // Redirect to dashboard on successful login
      history.push("/dashboard");
    } catch (error) {
      // Handle login errors and display appropriate message
      setResponse(
        <Alert className="error-message floating-alert" variant="light">
          <div>
            <div style={{ textAlign: "right" }}>
              <Button
                className="pitchdeckmessageclose"
                onClick={(event) => {
                  event.preventDefault();
                  setResponse("");
                }}
              >
                <IoMdClose />
              </Button>
            </div>
            <p>{error.message}</p>
          </div>
        </Alert>
      );
    }
  }

  /**
   * Handle resending verification email
   * Used when user hasn't confirmed their account
   * @param {string} email - Email address to send confirmation to
   */
  async function handleResendConfirmation(email) {
    // Show loading alert while processing
    setResponse(
      <Alert className="error-message floating-alert" variant="light">
        <div>
          <div style={{ textAlign: "right" }}>
            <Button
              className="pitchdeckmessageclose"
              onClick={(event) => {
                event.preventDefault();
                setResponse("");
              }}
            >
              <IoMdClose />
            </Button>
          </div>
          <div style={{ display: "flex", paddingTop: "10px" }}>
            <div style={{ width: "90%" }}>
              <p>
                <span style={{ color: "#0f9d58" }}>
                  <BiAnalyse className="alert-icon" /> Loading
                </span>
              </p>
              <p style={{ marginLeft: "28px", fontSize: "14px" }}>
                Attempting to log in
              </p>
              <p style={{ marginLeft: "28px", fontSize: "14px" }}>
                If your waiting time is too long,{" "}
                <a
                  href="https://www.roiquant.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  contact us
                </a>
                .
              </p>
            </div>
            <div style={{ width: "10%", textAlign: "right" }}>
              <Spinner animation="border" variant="secondary" />
            </div>
          </div>
        </div>
      </Alert>
    );

    try {
      await resendConfirmation(email, captchaTwoToken);

      setResponse(
        <Alert className="error-message floating-alert" variant="light">
          <div>
            <div style={{ textAlign: "right" }}>
              <Button
                className="pitchdeckmessageclose"
                onClick={(event) => {
                  event.preventDefault();
                  setResponse("");
                }}
              >
                <IoMdClose />
              </Button>
            </div>
            Confirmation email sent
          </div>
        </Alert>
      );
    } catch (error) {
      if (error.errorType) {
        setResponse(
          <Alert className="error-message floating-alert" variant="light">
            <div>
              <div style={{ textAlign: "right" }}>
                <Button
                  className="pitchdeckmessageclose"
                  onClick={(event) => {
                    event.preventDefault();
                    setResponse("");
                  }}
                >
                  <IoMdClose />
                </Button>
              </div>
              <p>{error.errorType}</p>
              <p>{error.errorMessage}</p>
            </div>
          </Alert>
        );
      } else {
        setResponse(
          <Alert className="error-message floating-alert" variant="light">
            <div>
              <div style={{ textAlign: "right" }}>
                <Button
                  className="pitchdeckmessageclose"
                  onClick={(event) => {
                    event.preventDefault();
                    setResponse("");
                  }}
                >
                  <IoMdClose />
                </Button>
              </div>
              Account verification error
            </div>
          </Alert>
        );
      }
    }
  }

  /**
   * Password validation utility functions
   * Each function returns a boolean indicating if the password meets the requirement
   */
  function checkPwdLength(password) {
    return isLength(password, { min: 8 });
  }

  // Get current password length for display
  function measurePwdLength(password) {
    return password.length;
  }

  // Check if password contains at least one special symbol
  function checkPwdSymbol(password) {
    return isStrongPassword(password, {
      minUppercase: 0,
      minLength: 0,
      minNumbers: 0,
      minSymbols: 1,
      minLowercase: 0,
    });
  }

  // Check if password contains at least one uppercase letter
  function checkPwdUppercase(password) {
    return isStrongPassword(password, {
      minUppercase: 1,
      minLength: 0,
      minNumbers: 0,
      minSymbols: 0,
      minLowercase: 0,
    });
  }

  // Check if password contains at least one lowercase letter
  function checkPwdLowercase(password) {
    return isStrongPassword(password, {
      minUppercase: 0,
      minLength: 0,
      minNumbers: 0,
      minSymbols: 0,
      minLowercase: 1,
    });
  }

  // Check if password contains at least one number
  function checkPwdNum(password) {
    return isStrongPassword(password, {
      minUppercase: 0,
      minLength: 0,
      minNumbers: 1,
      minSymbols: 0,
      minLowercase: 0,
    });
  }

  return (
    <LoginForm
      handleLogin={handleLogin}
      credentials={credentials}
      setCredentials={setCredentials}
      response={response}
      handleResendConfirmation={handleResendConfirmation}
      checkPwdLength={checkPwdLength}
      checkPwdSymbol={checkPwdSymbol}
      checkPwdUppercase={checkPwdUppercase}
      checkPwdLowercase={checkPwdLowercase}
      checkPwdNum={checkPwdNum}
      measurePwdLength={measurePwdLength}
      captchaToken={captchaToken}
      setCaptchaToken={setCaptchaToken}
      captchaTwoToken={captchaTwoToken}
      setCaptchaTwoToken={setCaptchaTwoToken}
    />
  );
}

export default LoginPage;
